import React from "react";
import { Ship, CheckCircle2, Clock, AlertTriangle, DollarSign, Package, ArrowUpRight, Loader } from "lucide-react";
import Sparkline from "./Sparkline";
import { mkSpark } from "./constants";

/**
 * Top row. With Excel data the first four cards are clickable and filter the shipment
 * table to the same bucket (`filter` is the table's ?filter= value).
 * shipmentsOnly (tenant workspace): no platform invoices / stock, so only the four shipment cards.
 */
export function TopKpiCards({ kpis, shipKpis, xl, metNote, loading, rowFilter, activeTab, activeView, onCardClick, shipmentsOnly = false }) {
    const grace = xl?.graceDays ?? 1;
    const cards = [
        { icon:Ship,          label:"Shipments In Transit",  value: shipKpis.shipmentsInTransit, sub:null,                            color:"#3B82F6", spark:"transit",
          filter:"intransit", rule:"Status is in transit, at or between collection and proof of delivery." },
        { icon:CheckCircle2,  label:"On Time",               value: shipKpis.onTimeCount,        sub:`${shipKpis.onTimePct ?? 0}%`,   color:"#10B981", spark:"ontime",
          filter:"ontime",
          rule:`Inbound: Actual Receipt Date no more than ${grace} day(s) after Expected Receipt (ERD), or not yet received and ERD not passed. Outbound: required delivery date not passed and booking not after it. % = on time ÷ (on time + delayed).`,
          note: metNote || (xl ? `${xl.noEta} with no target date yet` : null) },
        { icon:Clock,         label:"Delayed",               value: shipKpis.delayed,            sub:`${shipKpis.delayedPct ?? 0}%`,  color:"#F59E0B", spark:"delayed",
          filter:"delayed",
          rule:`Inbound: received more than ${grace} day(s) after Expected Receipt (ERD), or ERD passed and not yet received. Outbound: required delivery date passed, or booked/dispatching after it.` },
        { icon:AlertTriangle, label:"Exceptions",            value: shipKpis.exceptions,         sub:null,                            color:"#EF4444", spark:"exception",
          filter:"exceptions", rule:"Unrecognised status, ASN issue, corrupted delivery site, or an origin port in a destination field." },
        { icon:DollarSign,    label:"Invoices Pending",      value: kpis.pendingInvoices,    sub:null,                        color:"#8B5CF6", spark:"spend"     },
        { icon:Package,       label:"Low Stock SKUs",        value: kpis.lowStock,           sub:`of ${kpis.totalSKUs ?? 0}`, color:"#F97316", spark:"risk"      },
    ].filter(k => !shipmentsOnly || k.filter);

    return (
        <div className={`grid grid-cols-2 gap-3 ${shipmentsOnly ? "xl:grid-cols-4" : "sm:grid-cols-3 xl:grid-cols-6"}`}>
            {cards.map((k, i) => {
                const Icon = k.icon;
                const linked = xl && k.filter;
                const selected = linked && rowFilter === k.filter && activeTab !== "platform";
                return (
                    <div key={i}
                        onClick={linked ? () => onCardClick(k.filter) : undefined}
                        title={linked ? `${k.rule}\n\nClick to show these shipments${activeView === "all" ? ` — the table shows one report at a time (now: ${activeTab}); switch its tab for the other` : ""}.` : undefined}
                        className={`bg-[#111827] border rounded-xl p-4 flex flex-col gap-2 transition-colors ${
                            selected ? "border-blue-500/60" : "border-[#1F2937]"} ${linked ? "cursor-pointer hover:border-slate-500" : ""}`}>
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                                style={{backgroundColor:`${k.color}22`}}>
                                {loading
                                    ? <Loader size={13} className="animate-spin text-slate-600"/>
                                    : <Icon size={15} style={{color:k.color}}/>
                                }
                            </div>
                            <p className="text-[11px] text-slate-400 leading-tight">{k.label}</p>
                        </div>
                        <div>
                            <p className="text-xl font-bold text-white">
                                {loading || k.value == null
                                    ? <span className="text-slate-600">—</span>
                                    : k.value
                                }
                                {!loading && k.sub && k.value != null && (
                                    <span className="text-sm font-normal text-slate-400 ml-1.5">{k.sub}</span>
                                )}
                            </p>
                            {!loading && k.note && <p className="text-[10px] text-slate-500 mt-0.5">{k.note}</p>}
                        </div>
                        <Sparkline data={mkSpark(k.spark)} color={k.color}/>
                    </div>
                );
            })}
        </div>
    );
}

// Bottom row of volume / count cards (values come from bottomKpis in dashboardModel)
export function BottomKpiCards({ cards, loading }) {
    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
            {cards.map((k, i) => (
                <div key={i} className="bg-[#111827] border border-[#1F2937] rounded-xl p-4">
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">{k.label}</p>
                    <p className="text-xl font-bold text-white">
                        {loading || k.value == null ? "—" : k.value}
                    </p>
                    <p className="text-[11px] mt-1 flex items-center gap-1" style={{color:k.color}}>
                        <ArrowUpRight size={11}/> {k.sub}
                    </p>
                    <div className="mt-2">
                        <Sparkline data={mkSpark("transit")} color={k.color}/>
                    </div>
                </div>
            ))}
        </div>
    );
}
